import { motion, AnimatePresence } from "framer-motion";
import { Mail, X, MessageSquare } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ContactModal = ({ isOpen, onClose }: ContactModalProps) => {
  const { toast } = useToast();

  const handleWhatsAppClick = () => {
    toast({
      title: "Opening WhatsApp",
      description: "Redirecting you to WhatsApp...",
      duration: 3000,
    });
    setTimeout(() => {
      window.open(
        "https://wa.me/254758214490?text=Hello%20Koech%20Creatives%2C%20I%20just%20viewed%20your%20website%20and%20I'm%20impressed%20with%20your%20work.%20I'd%20like%20to%20discuss%20a%20potential%20project.",
        "_blank"
      );
    }, 500);
    onClose();
  };

  const handleEmailClick = () => {
    toast({
      title: "Opening Email",
      description: "Redirecting you to your email client...",
      duration: 3000,
    });
    setTimeout(() => {
      window.open(
        "mailto:koechcreatives@gmail.com?subject=Project%20Inquiry%20from%20Website&body=Hello%20Koech%20Creatives%2C%0A%0AI%20recently%20visited%20your%20website%20and%20I'm%20impressed%20with%20your%20portfolio.%20I'd%20like%20to%20discuss%20a%20potential%20collaboration%20on%20a%20project%20I%20have%20in%20mind.%0A%0APlease%20let%20me%20know%20when%20would%20be%20a%20good%20time%20to%20connect.%0A%0ARegards%2C",
        "_blank"
      );
    }, 500);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-end md:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          {/* Modal Content */}
          <motion.div
            className="bg-gradient-to-br from-accent-navy to-accent-navy/95 w-full max-w-md md:rounded-2xl md:m-4 rounded-t-2xl p-6 shadow-2xl border border-gray-800"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Let's Make Something Great Together
              </h2>
              <p className="text-gray-300 text-sm md:text-base">
                Reach us instantly — we usually reply within the hour.
              </p>
            </div>

            {/* Contact Options */}
            <div className="flex flex-col space-y-4">
              <motion.button
                className="flex items-center justify-center bg-[#25D366] text-white py-4 px-6 rounded-xl font-medium"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                onClick={handleWhatsAppClick}
              >
                <MessageSquare className="w-5 h-5 mr-3" />
                Message on WhatsApp
              </motion.button>

              <motion.button
                className="flex items-center justify-center bg-coral text-white py-4 px-6 rounded-xl font-medium"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                onClick={handleEmailClick}
              >
                <Mail className="w-5 h-5 mr-3" />
                Send us an Email
              </motion.button>
            </div>

            {/* Additional Info */}
            <p className="text-gray-400 text-xs mt-6 text-center">
              We're excited to hear about your project and how we can help bring your vision to life.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}; 